import { omit } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toShipmentInputData } from "../../components/shipment/EditShipmentForm";
import ShipmentForm from "../../components/shipment/ShipmentForm";
import {
  BusinessEntityType,
  LoadType,
  ShipmentCommodityType,
  ShipmentLocationType,
  TransactionType,
  useAddBusinessEntityMutation,
  useCreateShipmentMutation,
  useGetBusinessEntitiesQuery,
  useGetBusinessEntityListQuery,
  useGetLoadBoardLoadDetailsQuery,
  useGetOrganizationSettingsQuery,
  useGetOrderDetailsQuery,
  useGetShipmentQuery,
} from "../../graphql/generated";
import { ShipmentView } from "../../utils/shipment/shipmentView";
import { showDialog } from "../../redux/slices/alert/Alert.slice";
import {
  editShipment,
  editShipmentLocationInput,
  patchShipment,
  resetShipmentForm,
  setCharges,
  setCustomer,
  setShipperGoods,
} from "../../redux/slices/shipment/ShipmentForm.slice";
import LoadingOverlay from "../../components/common/LoadingOverlay";
import { RootState } from "../../redux/store";
import { v4 as uuid } from "uuid";
import { ShipmentLocationInputData } from "../../redux/slices/Types";
import isLiquidUnit from "../../utils/liquid/isLiquidUnit";
import { resolveDefaultLiquidTrailerTypes } from "../../utils/liquid/resolveDefaultLiquidTrailerTypes";
import { useTranslation } from "react-i18next";
import { endOfDay, isValid } from "date-fns";
import { localWeightToKg } from "../../utils/conversion/weight";
import { queryClient } from "../../providers/QueryClient";
import {
  Feature,
  useFeature,
} from "../../components/account/Access/FeatureGuard";

export default function CreateShipment() {
  const { t } = useTranslation(["orders", "common"]);
  const mutation = useCreateShipmentMutation();
  const [isUploadingDocuments, setIsUploadingDocuments] = useState(false);
  const navigate = useNavigate();
  const navigation = useLocation();
  const [searchParams] = useSearchParams();
  const duplicatedShipmentId =
    searchParams.get("duplicatedShipmentId") || navigation.state?.shipment;
  const shipmentQuery = useGetShipmentQuery(
    { id: duplicatedShipmentId || "" },
    {
      refetchOnWindowFocus: false,
      enabled: !!duplicatedShipmentId,
    }
  );
  const [params] = useSearchParams();
  const orderId = params.get("orderId");
  const orderQuery = useGetOrderDetailsQuery(
    { id: orderId || "" },
    {
      refetchOnWindowFocus: false,
      enabled: !!orderId,
    }
  );
  const loadBoardShipmentQuery = useGetLoadBoardLoadDetailsQuery(
    {
      loadId: navigation.state?.loadBoardShipmentId || "",
    },
    {
      refetchOnWindowFocus: false,
    }
  );
  const shipment: ShipmentView | undefined =
    shipmentQuery.data?.shipmentById ||
    loadBoardShipmentQuery.data?.loadBoardLoadById ||
    undefined;
  const stateShipment = useSelector(
    (state: RootState) => state.shipmentReducer.shipment
  );
  const [hasReset, setHasReset] = useState(false);
  const [hasPreloaded, setHasPreloaded] = useState(false);
  const dispatch = useDispatch();
  const orgSettingsQuery = useGetOrganizationSettingsQuery(
    {},
    { refetchOnWindowFocus: false }
  );
  const defaultLoadType =
    orgSettingsQuery.data?.organizationSettings?.dispatching
      ?.orderTypeDefault ?? LoadType.FullTruckLoad;
  const defaultTrailerTypesSetting =
    orgSettingsQuery.data?.organizationSettings?.dispatching
      ?.defaultTrailerTypes;
  const defaultLiquidTrailers = useMemo(
    () => resolveDefaultLiquidTrailerTypes(defaultTrailerTypesSetting),
    [defaultTrailerTypesSetting]
  );
  const commodityManagementFeature = useFeature(Feature.CommodityManagement);

  useEffect(() => {
    dispatch(resetShipmentForm());
    setHasReset(true);
    if (shipment) {
      dispatch(
        editShipment({
          ...omit(
            toShipmentInputData({
              ...shipment,
              shipmentNumber: "",
              documents: [],
              charges: navigation.state?.loadBoardShipmentId
                ? shipment.charges
                : [],
            }),
            "_id",
            "shipmentNumber",
            "status",
            "tripId",
            "reasonForCancellation"
          ),
          customer:
            (navigation.state?.customerId as string | undefined) ||
            shipment.customer._id,
        })
      );
    }
  }, [
    shipment,
    dispatch,
    navigation.state?.customerId,
    navigation.state?.loadBoardShipmentId,
  ]);

  useEffect(() => {
    dispatch(patchShipment({ loadType: defaultLoadType }));
  }, [dispatch, defaultLoadType]);

  useEffect(() => {
    if (!commodityManagementFeature.isAvailable) {
      return;
    }
    dispatch(patchShipment(defaultLiquidTrailers));
  }, [
    dispatch,
    defaultLiquidTrailers,
    commodityManagementFeature.isAvailable,
  ]);

  useEffect(() => {
    async function preloadShipmentInfoFromQuery() {
      const customerName = searchParams.get("customerName");
      if (customerName) {
        const existingCustomers = await useGetBusinessEntityListQuery.fetcher({
          businessEntityTypes: [BusinessEntityType.Customer],
          filter: {
            name: customerName,
          },
        })();

        const existingCustomer = existingCustomers.businessEntities.data?.[0];

        if (existingCustomer) {
          dispatch(setCustomer(existingCustomer._id));
        } else {
          const {
            addBusinessEntity: { _id: newCustomerId },
          } = await useAddBusinessEntityMutation.fetcher({
            newBusinessEntityData: {
              name: customerName,
              type: BusinessEntityType.Customer,
              address: {
                label: "N/A",
                coordinates: {
                  latitude: 0,
                  longitude: 0,
                },
              },
            },
          })();
          queryClient.refetchQueries(useGetBusinessEntitiesQuery.getKey());
          dispatch(setCustomer(newCustomerId));
        }

        const shipperLocationLabel = searchParams.get("shipperLocationLabel");
        const shipperAddressLat = searchParams.get("shipperAddressLat");
        const shipperAddressLng = searchParams.get("shipperAddressLng");
        const shipperTimeWindowStart = searchParams.get(
          "shipperTimeWindowStart"
        );
        const shipperTimeWindowEnd = searchParams.get("shipperTimeWindowEnd");

        if (shipperLocationLabel && shipperAddressLat && shipperAddressLng) {
          const shipperLocation = stateShipment.shipmentLocations.find(
            (location) => location.locationType === ShipmentLocationType.Pickup
          );
          if (shipperLocation) {
            dispatch(
              editShipmentLocationInput({
                location: {
                  ...shipperLocation,
                  name: shipperLocationLabel,
                  addressLabel: shipperLocationLabel,
                  location: {
                    latitude:
                      shipperAddressLat && parseFloat(shipperAddressLat)
                        ? parseFloat(shipperAddressLat)
                        : shipperLocation.location.latitude,
                    longitude:
                      shipperAddressLng && parseFloat(shipperAddressLng)
                        ? parseFloat(shipperAddressLng)
                        : shipperLocation.location.longitude,
                  },
                  timeWindows:
                    shipperTimeWindowStart &&
                    shipperTimeWindowEnd &&
                    isValid(new Date(shipperTimeWindowStart)) &&
                    isValid(new Date(shipperTimeWindowEnd))
                      ? [
                          {
                            fromDate: new Date(shipperTimeWindowStart),
                            toDate: endOfDay(new Date(shipperTimeWindowEnd)),
                          },
                        ]
                      : [],
                },
              })
            );
          }
        }

        const receiverLocationLabel = searchParams.get("receiverLocationLabel");
        const receiverAddressLat = searchParams.get("receiverAddressLat");
        const receiverAddressLng = searchParams.get("receiverAddressLng");
        const receiverTimeWindowStart = searchParams.get(
          "receiverTimeWindowStart"
        );
        const receiverTimeWindowEnd = searchParams.get("receiverTimeWindowEnd");

        if (receiverLocationLabel && receiverAddressLat && receiverAddressLng) {
          const receiverLocation = stateShipment.shipmentLocations.find(
            (location) => location.locationType === ShipmentLocationType.DropOff
          );
          if (receiverLocation) {
            dispatch(
              editShipmentLocationInput({
                location: {
                  ...receiverLocation,
                  name: receiverLocationLabel,
                  addressLabel: receiverLocationLabel,
                  location: {
                    latitude:
                      receiverAddressLat && parseFloat(receiverAddressLat)
                        ? parseFloat(receiverAddressLat)
                        : receiverLocation.location.latitude,
                    longitude:
                      receiverAddressLng && parseFloat(receiverAddressLng)
                        ? parseFloat(receiverAddressLng)
                        : receiverLocation.location.longitude,
                  },
                  timeWindows:
                    receiverTimeWindowStart &&
                    receiverTimeWindowEnd &&
                    isValid(new Date(receiverTimeWindowStart)) &&
                    isValid(new Date(receiverTimeWindowEnd))
                      ? [
                          {
                            fromDate: new Date(receiverTimeWindowStart),
                            toDate: endOfDay(new Date(receiverTimeWindowEnd)),
                          },
                        ]
                      : [],
                },
              })
            );
          }
        }

        const commodityWeight = searchParams.get("commodityWeight");
        if (commodityWeight) {
          const shipperLocation = stateShipment.shipmentLocations.find(
            (location) => location.locationType === ShipmentLocationType.Pickup
          );
          if (shipperLocation) {
            dispatch(
              setShipperGoods({
                _id: shipperLocation._id,
                goods: [
                  {
                    _id: uuid(),
                    label: "Commodity",
                    quantity: 1,
                    weight:
                      commodityWeight && parseFloat(commodityWeight)
                        ? localWeightToKg(parseFloat(commodityWeight))
                        : localWeightToKg(1),
                  },
                ],
              })
            );
          }
        }

        const paidAmount = parseFloat(searchParams.get("paidAmount") || "");
        if (paidAmount && !stateShipment.charges.length) {
          dispatch(
            setCharges([
              {
                label: "Linehaul charge",
                rate: paidAmount,
                unit: 1,
                unitType: "USD",
                type: TransactionType.Income,
              },
            ])
          );
        }
      }

      setHasPreloaded(true);
    }
    preloadShipmentInfoFromQuery();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, dispatch]);

  const order = orderQuery.data?.orderById;
  useEffect(() => {
    if (order) {
      const isLiquid = order.locations.some((location) =>
        location.receivedGoods?.some(
          (good) =>
            good.goodProfile?.unit && isLiquidUnit(good.goodProfile.unit)
        )
      );
      const shipperLocation: ShipmentLocationInputData = {
        ...stateShipment.shipmentLocations[0],
        shippedGoods: (order.locations[0].receivedGoods || []).map(
          (receivedGood) => ({
            _id: receivedGood.goodId,
            goodProfileId: receivedGood.goodProfile?._id,
            quantity: receivedGood.quantity || 1,
            unit: receivedGood.goodProfile?.unit,
            label: receivedGood.goodProfile?.label || "",
            weight: receivedGood.goodProfile?.weight,
          })
        ),
      };
      dispatch(
        editShipment({
          ...stateShipment,
          customer: order.customerId,
          referenceNumbers: [order.orderNumber],
          orderId: order._id,
          billOfLadingNumbers: [""],
          purchaseOrderNumbers: [""],
          shipmentLocations: [shipperLocation].concat(
            order.locations.map((location) => {
              return {
                ...location,
                _id: location._id || uuid(),
                receivedGoods: (location.receivedGoods || []).map(
                  (receivedGood) => omit(receivedGood, "goodProfile")
                ),
                receiver: location.receiver?._id,
                shippedGoods: [],
                shipper: null,
              };
            })
          ),
          commodityType: isLiquid
            ? ShipmentCommodityType.Liquid
            : ShipmentCommodityType.Dry,
          trailerType: isLiquid
            ? defaultLiquidTrailers.trailerType
            : stateShipment.trailerType,
          additionalTrailerTypes: isLiquid
            ? defaultLiquidTrailers.additionalTrailerTypes
            : stateShipment.additionalTrailerTypes,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order]);

  if (!hasReset) {
    return <LoadingOverlay loading />;
  }
  if (!hasPreloaded) {
    return <LoadingOverlay loading />;
  }
  if (
    navigation.state?.shipment &&
    (shipmentQuery.isLoading || !stateShipment.id)
  ) {
    return <LoadingOverlay loading />;
  }

  if (
    navigation.state?.loadBoardShipmentId &&
    (loadBoardShipmentQuery.isLoading || !stateShipment.id)
  ) {
    return <LoadingOverlay loading />;
  }

  return (
    <ShipmentForm
      loading={isUploadingDocuments || mutation.isLoading}
      onSubmit={async (_shipment) => {
        try {
          if (!_shipment) {
            return;
          }
          // if (newDocumentsToUpload) {
          //   setIsUploadingDocuments(true);
          //   await Promise.all(
          //     newDocumentsToUpload.map(async (d) => {
          //       await fetch(d.url, {
          //         method: "PUT",
          //         body: d.file,
          //       });
          //     })
          //   );
          //   setIsUploadingDocuments(false);
          // }
          const createShipmentResponse = await mutation.mutateAsync({
            shipment: _shipment,
          });
          dispatch(resetShipmentForm());
          navigate(`/orders/details/${createShipmentResponse.addShipment._id}`);
          window.analytics?.track("Load Created", {
            loadId: createShipmentResponse.addShipment._id,
          });
          window.analytics?.identify({
            loadCreated: true,
            lastLoadCreationDate: new Date(),
            lastLoadCreationDateOnly: new Date().toISOString().split("T")[0],
            numberOfLoadsCreated:
              (window.analytics?.user?.()?.traits?.()?.numberOfLoadsCreated ||
                0) + 1,
          });
          window.analytics?.group(window.analytics?.group?.()?.id?.(), {
            loadCreated: true,
            lastLoadCreationDate: new Date(),
            lastLoadCreationDateOnly: new Date().toISOString().split("T")[0],
            numberOfLoadsCreated:
              (window.analytics?.group?.()?.traits?.()?.numberOfLoadsCreated ||
                0) + 1,
          });
        } catch (error) {
          dispatch(
            showDialog({
              type: "error",
              title: t(
                "error.createShipmentError",
                "Error while creating shipment"
              ),
              description:
                (error as Error).message ||
                t("common:error.unknownError", "Unknown error"),
            })
          );
        } finally {
          setIsUploadingDocuments(false);
        }
      }}
    />
  );
}
